import { useEffect, useMemo, useState } from 'react';

import type { ProductItem } from '@asb/shared';

import { useAppStore } from '../lib/store';
import { t } from '../lib/translations';
import RecommendationCard from './RecommendationCard';

/**
 * Renders the recommendations section.
 *
 * Three states:
 *   1. idle (no submit yet, or query reset) → return null, section hidden
 *   2. generating (isGenerating=true) → 3 skeleton cards + rotating status text
 *      so the audience sees visible work happening during the 2-4s LLM call
 *   3. resolved (solution set) → real cards with provider/latency meta subtitle
 *
 * The skeleton lives here (not RecommendationCard) because the layout
 * dimensions need to match — same 3-col grid, same min-height — to avoid
 * layout shift when real cards land.
 */
export default function RecommendationGrid({ products }: { products: ProductItem[] }) {
  const lang = useAppStore((s) => s.lang);
  const brand = useAppStore((s) => s.brand);
  const solution = useAppStore((s) => s.solution);
  const isGenerating = useAppStore((s) => s.isGenerating);
  const ui = t(lang);

  // Status text rotation during loading. Each phase ~800ms; if the actual
  // network call finishes faster we just see "analysing" briefly. If slower,
  // the rotation gives the audience a sense of progressive work even though
  // the underlying LLM call is one shot.
  const STATUS_PHASES: ReadonlyArray<keyof typeof ui> = [
    'recommendationsLoadingPhase1',
    'recommendationsLoadingPhase2',
    'recommendationsLoadingPhase3',
  ] as const;
  const [phaseIdx, setPhaseIdx] = useState(0);
  useEffect(() => {
    if (!isGenerating) {
      setPhaseIdx(0);
      return;
    }
    const id = window.setInterval(() => {
      // Don't loop past last phase; stay on phase 3 ("generating recommendations")
      // so a long call doesn't keep restarting at "analysing".
      setPhaseIdx((prev) => Math.min(prev + 1, STATUS_PHASES.length - 1));
    }, 900);
    return () => window.clearInterval(id);
  }, [isGenerating, STATUS_PHASES.length]);

  const picks = useMemo(() => {
    if (!solution) return [] as Array<{ product: ProductItem; rationale: string }>;
    const byId = new Map(products.map((p) => [p.id, p]));
    return solution.selectedProducts
      .map((id) => {
        const product = byId.get(id);
        if (!product) return null;
        return { product, rationale: solution.rationale[id] ?? '' };
      })
      .filter((x): x is { product: ProductItem; rationale: string } => x !== null);
  }, [solution, products]);

  // ── Loading skeleton — 3 placeholder cards while the API is in flight ──
  if (isGenerating && !solution) {
    return (
      <section className="mt-10" aria-busy="true" aria-live="polite">
        <div className="mb-3 flex items-baseline justify-between gap-3">
          <h2 className="text-sm font-medium text-slate-700">{ui.recommendationsTitle}</h2>
          <span className="text-[11px] text-slate-500 tabular-nums">
            {ui[STATUS_PHASES[phaseIdx]!] as string}
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="rounded-2xl border border-slate-200 bg-white p-5 min-h-[14rem] shadow-sm animate-pulse"
              aria-hidden="true"
            >
              <div className="h-5 w-2/3 rounded bg-slate-200" />
              <div className="mt-3 h-3 w-full rounded bg-slate-100" />
              <div className="mt-2 h-3 w-5/6 rounded bg-slate-100" />
              <div className="mt-2 h-3 w-3/4 rounded bg-slate-100" />
              <div className="mt-6 h-8 w-24 rounded-full bg-slate-200" />
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (!solution || picks.length === 0) return null;

  // NOTE: 之前这里有 "由 <model> 生成 · 1.2s" 的 subtitle，2026-06 应要求移除
  // —— Summit 观众不需要看到底层用了哪个模型 / 多慢（25s 这种延迟显示出来也
  // 尴尬）。solution.model / latencyMs 字段仍在 API response 里，讲师可在
  // Network tab 验证链路没 silently fall through，只是不再渲染给观众。
  return (
    <section className="mt-10">
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <h2 className="text-sm font-medium text-slate-600">{ui.recommendationsTitle}</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {picks.map(({ product, rationale }, i) => (
          <RecommendationCard
            key={product.id}
            product={product}
            rationale={rationale}
            lang={lang}
            brand={brand}
            rank={i}
          />
        ))}
      </div>
    </section>
  );
}
